import React, { createContext, useContext, useState, useEffect, useMemo, useRef, ReactNode } from "react";
import { Dupla, GameState, HistoricoRodada, Tournament } from "../types";
import { SavedSession } from "../components/MatchHistory";
import { formatTeamName, createTeamFromPlayers } from "../utils/teamUtils";
import { computeNextRachaoState } from "../utils/rachaoEngine";
import { loadRachaoData, saveRachaoData, exportToJSON, exportToExcel } from "../utils/storage";
import {
  isFirebaseConfigured,
  subscribeDuplas,
  subscribeGameState,
  subscribeHistorico,
  saveDuplaFirestore,
  deleteDuplaFirestore,
  saveGameStateFirestore,
  addHistoricoFirestore,
  clearHistoricoFirestore,
  subscribeTorneios,
  subscribePublicTournament,
  saveTorneioFirestore,
  deleteTorneioFirestore,
  subscribeSystemSettingsFirestore,
} from "../utils/firebase";
import { applyRemoteSettings } from "../utils/commissionConfig";
import { useAuth } from "./AuthContext";
import { useApp } from "./AppContext";
import { StorageService, STORAGE_KEYS } from "../services/storage";

function getLocalHostId(): string {
  if (typeof window === "undefined") return "guest_host";
  let id = StorageService.getRaw(STORAGE_KEYS.RACHAO_HOST_ID);
  if (!id) {
    id = `host_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
    StorageService.setRaw(STORAGE_KEYS.RACHAO_HOST_ID, id);
  }
  return id;
}

export interface GameContextType {
  isInitialized: boolean;
  duplas: Dupla[];
  globalDuplas: Dupla[];
  gameState: GameState;
  setGameState: React.Dispatch<React.SetStateAction<GameState>>;
  historico: HistoricoRodada[];
  torneios: Tournament[];
  sessoes: SavedSession[];
  matchSeconds: number;
  isTimerRunning: boolean;
  formatTime: (totalSecs: number) => string;
  getEffectiveCloudUid: () => string;
  handleAddDupla: (
    nameOrDupla: string | Dupla,
    playersArr?: string[],
    tournamentId?: string,
    customId?: string
  ) => Dupla | null;
  handleEditDupla: (id: string, name: string, players: string[]) => void;
  handleDeleteDupla: (id: string) => void;
  handleDuplicateDupla: (dupla: Dupla) => void;
  handleApplySorteio: (novasDuplas: Dupla[], equipeIncompleta: string) => void;
  handleStartRachao: (newState: GameState) => void;
  handleEndRachao: () => void;
  handleReactivateSession: (session: SavedSession) => void;
  handleDeleteSession: (sessionId: string) => void;
  handleClearAllSessions: () => void;
  handleConfirmRound: (
    vencedoraId: string,
    jogadorTransferido: string,
    scoreTeam1?: number,
    scoreTeam2?: number
  ) => void;
  handleUpdateQueue: (newFilaIds: string[]) => void;
  handleUpdateDuplas: (novasDuplas: Dupla[]) => void;
  handleUpdateGameState: (novoState: GameState) => void;
  handleSaveTorneio: (torneioInput: Tournament) => void;
  handleDeleteTorneio: (id: string) => void;
  handleImportJSON: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleClearHistory: () => void;
  handleResetAllData: () => void;
  exportJSON: () => void;
  exportExcel: () => void;
}

const GameContext = createContext<GameContextType | undefined>(undefined);

export const GameProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, userProfile } = useAuth();
  const { activeTab, rachaHostId, showToast, setActiveTab } = useApp();

  const [isInitialized, setIsInitialized] = useState(false);
  const [duplas, setDuplas] = useState<Dupla[]>([]);
  const globalDuplas = useMemo(() => {
    return duplas.filter((d) => !d.tournamentId);
  }, [duplas]);

  const [gameState, setGameState] = useState<GameState>({
    emAndamento: false,
    jogoAtual: null,
    primeiraProxima: null,
    equipeIncompleta: "",
    fila: [],
  });

  const [historico, setHistorico] = useState<HistoricoRodada[]>([]);
  const [torneios, setTorneios] = useState<Tournament[]>([]);

  // Sessões Salvas de Rachão
  const [sessoes, setSessoes] = useState<SavedSession[]>(() => {
    return StorageService.get<SavedSession[]>(STORAGE_KEYS.RACHAO_SESSOES, []) || [];
  });

  const saveSessoes = (data: SavedSession[]) => {
    setSessoes(data);
    StorageService.set(STORAGE_KEYS.RACHAO_SESSOES, data);
  };

  // Cronômetro
  const [matchSeconds, setMatchSeconds] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Auxiliares LocalStorage para Torneios
  const loadLocalTorneios = (): Tournament[] => {
    return StorageService.get<Tournament[]>(STORAGE_KEYS.RACHAO_TORNEIOS, []) || [];
  };

  const saveLocalTorneios = (data: Tournament[]) => {
    StorageService.set(STORAGE_KEYS.RACHAO_TORNEIOS, data);
  };

  const getEffectiveCloudUid = (): string => {
    return (
      rachaHostId ||
      user?.uid ||
      (userProfile?.id && !userProfile.id.startsWith("guest") ? userProfile.id : null) ||
      getLocalHostId()
    );
  };

  // Assina sincronização cross-tab via BroadcastChannel
  useEffect(() => {
    let channel: BroadcastChannel | null = null;
    try {
      channel = new BroadcastChannel("rachao_realtime_sync");
      channel.onmessage = (event) => {
        if (event.data?.type === "TORNEIO_UPDATED" && event.data.torneio) {
          const updatedT: Tournament = event.data.torneio;
          setTorneios((prev) => {
            const exists = prev.some((t) => t.id === updatedT.id);
            if (exists) {
              return prev.map((t) => (t.id === updatedT.id ? { ...t, ...updatedT } : t));
            }
            return [updatedT, ...prev];
          });
        } else if (event.data?.type === "TORNEIO_DELETED" && event.data.id) {
          const deletedId = event.data.id;
          setTorneios((prev) => prev.filter((t) => t.id !== deletedId));
        }
      };
    } catch (e) {}

    return () => {
      if (channel) channel.close();
    };
  }, []);

  // Carrega dados e assina listeners do Firebase
  useEffect(() => {
    let unsubDuplas = () => {};
    let unsubGameState = () => {};
    let unsubHistorico = () => {};
    let unsubTorneios = () => {};
    let unsubSettings = () => {};

    const hasFirebase = isFirebaseConfigured();

    if (hasFirebase) {
      unsubSettings = subscribeSystemSettingsFirestore((cloudSettings) => {
        if (cloudSettings) {
          applyRemoteSettings(cloudSettings);
        }
      });

      const authenticatedUid = user?.uid || (userProfile?.id && !userProfile.id.startsWith("guest") && !userProfile.id.startsWith("host_") ? userProfile.id : null);
      const effectiveUid = rachaHostId || authenticatedUid;

      if (effectiveUid) {
        unsubDuplas = subscribeDuplas(
          effectiveUid,
          (data) => {
            setDuplas(data);
            setIsInitialized(true);
          },
          []
        );

        unsubGameState = subscribeGameState(
          effectiveUid,
          (state) => {
            setGameState(state);
            setIsTimerRunning(state.emAndamento);
          },
          {
            emAndamento: false,
            jogoAtual: null,
            primeiraProxima: null,
            equipeIncompleta: "",
            fila: [],
          }
        );

        unsubHistorico = subscribeHistorico(
          effectiveUid,
          (hist) => {
            setHistorico(hist);
          },
          []
        );

        unsubTorneios = subscribeTorneios(
          effectiveUid,
          (tList) => {
            setTorneios(tList);
          },
          loadLocalTorneios()
        );
      } else {
        const { duplas: localD, gameState: localGS, historico: localH } = loadRachaoData();
        setDuplas(localD);
        setGameState(localGS);
        setHistorico(localH);
        setTorneios(loadLocalTorneios());
        setIsTimerRunning(localGS.emAndamento);
        setIsInitialized(true);
      }
    } else {
      const { duplas: localD, gameState: localGS, historico: localH } = loadRachaoData();
      setDuplas(localD);
      setGameState(localGS);
      setHistorico(localH);
      setTorneios(loadLocalTorneios());
      setIsTimerRunning(localGS.emAndamento);
      setIsInitialized(true);
    }

    return () => {
      unsubSettings();
      unsubDuplas();
      unsubGameState();
      unsubHistorico();
      unsubTorneios();
    };
  }, [user, rachaHostId, userProfile?.id]);

  // Salva no LocalStorage para modo offline
  useEffect(() => {
    if (isInitialized && !user) {
      saveRachaoData(duplas, gameState, historico);
      saveLocalTorneios(torneios);
    }
  }, [duplas, gameState, historico, torneios, isInitialized, user]);

  // Timer effect
  useEffect(() => {
    if (isTimerRunning && gameState.emAndamento) {
      timerRef.current = setInterval(() => {
        setMatchSeconds((prev) => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isTimerRunning, gameState.emAndamento]);

  // Cloud Helpers
  const cloudSaveDupla = (nova: Dupla) => {
    const uid = getEffectiveCloudUid();
    if (isFirebaseConfigured() && uid) {
      saveDuplaFirestore(uid, nova);
    }
  };

  const cloudDeleteDupla = (id: string) => {
    const uid = getEffectiveCloudUid();
    if (isFirebaseConfigured() && uid) {
      deleteDuplaFirestore(uid, id);
    }
  };

  const cloudSaveGameState = (state: GameState) => {
    const uid = getEffectiveCloudUid();
    if (isFirebaseConfigured() && uid) {
      saveGameStateFirestore(uid, state);
    }
  };

  const cloudAddHistorico = (item: HistoricoRodada) => {
    const uid = getEffectiveCloudUid();
    if (isFirebaseConfigured() && uid) {
      addHistoricoFirestore(uid, item);
    }
  };

  const cloudClearHistorico = () => {
    const uid = getEffectiveCloudUid();
    if (isFirebaseConfigured() && uid) {
      clearHistoricoFirestore(uid);
    }
  };

  // Handlers
  const handleAddDupla = (
    nameOrDupla: string | Dupla,
    playersArr?: string[],
    tournamentId?: string,
    customId?: string
  ) => {
    let nova: Dupla;
    if (typeof nameOrDupla === "object") {
      nova = nameOrDupla;
    } else {
      const name = nameOrDupla;
      const players = playersArr || [];
      if (duplas.some((d) => d.name.toLowerCase() === name.toLowerCase())) {
        showToast("Já existe uma equipe cadastrada com este nome!", "error");
        return null;
      }
      nova = {
        id: customId || `dupla_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
        name,
        player1: players[0] || "",
        player2: players[1] || "",
        player3: players[2],
        player4: players[3],
        player5: players[4],
        player6: players[5],
        player7: players[6],
        player8: players[7],
        player9: players[8],
        player10: players[9],
        player11: players[10],
        player12: players[11],
        player13: players[12],
        player14: players[13],
        player15: players[14],
        tournamentId,
        createdAt: Date.now(),
      };
    }

    setDuplas((prev) => [...prev, nova]);
    cloudSaveDupla(nova);
    showToast(`Equipe "${nova.name}" adicionada com sucesso!`);
    return nova;
  };

  const handleEditDupla = (id: string, name: string, players: string[]) => {
    const updatedFields = {
      name,
      player1: players[0] || "",
      player2: players[1] || "",
      player3: players[2] || undefined,
      player4: players[3] || undefined,
      player5: players[4] || undefined,
      player6: players[5] || undefined,
      player7: players[6] || undefined,
      player8: players[7] || undefined,
      player9: players[8] || undefined,
      player10: players[9] || undefined,
      player11: players[10] || undefined,
      player12: players[11] || undefined,
      player13: players[12] || undefined,
      player14: players[13] || undefined,
      player15: players[14] || undefined,
    };

    setDuplas((prev) =>
      prev.map((d) => (d.id === id ? { ...d, ...updatedFields } : d))
    );
    const d = duplas.find((item) => item.id === id);
    if (d) {
      cloudSaveDupla({ ...d, ...updatedFields });
    }
    showToast("Equipe atualizada!");
  };

  const handleDeleteDupla = (id: string) => {
    const duplaExcluida = duplas.find((d) => d.id === id);
    if (!duplaExcluida) return;

    if (
      window.confirm(
        `Tem certeza que deseja excluir a equipe "${duplaExcluida.name}"? Se houver jogo em andamento, ela será removida da fila.`
      )
    ) {
      setDuplas((prev) => prev.filter((d) => d.id !== id));
      cloudDeleteDupla(id);

      let jogoAtual = gameState.jogoAtual;
      let primeiraProxima = gameState.primeiraProxima;
      let fila = gameState.fila;

      if (jogoAtual && (jogoAtual[0] === id || jogoAtual[1] === id)) {
        jogoAtual = null;
      }
      if (primeiraProxima === id) {
        primeiraProxima = null;
      }
      fila = fila.filter((duplaId) => duplaId !== id);

      const novoState = {
        ...gameState,
        jogoAtual,
        primeiraProxima,
        fila,
        emAndamento: jogoAtual !== null,
      };

      setGameState(novoState);
      cloudSaveGameState(novoState);

      showToast("Equipe excluída com sucesso!");
    }
  };

  const handleDuplicateDupla = (dupla: Dupla) => {
    const baseName = dupla.name.replace(/ \(Cópia\d*\)/, "");
    let count = 1;
    let newName = `${baseName} (Cópia ${count})`;

    while (duplas.some((d) => d.name.toLowerCase() === newName.toLowerCase())) {
      count++;
      newName = `${baseName} (Cópia ${count})`;
    }

    const nova: Dupla = {
      id: `dupla_copy_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
      name: newName,
      player1: dupla.player1,
      player2: dupla.player2,
      player3: dupla.player3,
      player4: dupla.player4,
      player5: dupla.player5,
      player6: dupla.player6,
      player7: dupla.player7,
      player8: dupla.player8,
      player9: dupla.player9,
      player10: dupla.player10,
      player11: dupla.player11,
      player12: dupla.player12,
      player13: dupla.player13,
      player14: dupla.player14,
      player15: dupla.player15,
      createdAt: Date.now(),
    };

    setDuplas((prev) => [...prev, nova]);
    cloudSaveDupla(nova);
    showToast(`Equipe "${dupla.name}" duplicada!`);
  };

  const handleApplySorteio = (novasDuplas: Dupla[], equipeIncompleta: string) => {
    const duplasAntigas = [...duplas];
    duplasAntigas.forEach((d) => cloudDeleteDupla(d.id));

    setDuplas(novasDuplas);
    novasDuplas.forEach((d) => cloudSaveDupla(d));

    const idsDuplas = novasDuplas.map((d) => d.id);
    const jogoAtual: [string, string] | null =
      idsDuplas.length >= 2 ? [idsDuplas[0], idsDuplas[1]] : null;
    const primeiraProxima = idsDuplas.length >= 3 ? idsDuplas[2] : null;
    const fila = idsDuplas.length >= 4 ? idsDuplas.slice(3) : [];

    const novoState: GameState = {
      emAndamento: idsDuplas.length >= 2,
      jogoAtual,
      primeiraProxima,
      equipeIncompleta: equipeIncompleta,
      fila,
      vitoriasConsecutivas: 0,
      idEquipeVencedoraAtual: null,
      regras: gameState.regras || {
        tamanhoEquipe: 2,
        modoPlay: "solo",
        limiteVitorias: 0,
        destinoVencedorLimite: "primeira_proxima",
      },
    };

    setGameState(novoState);
    cloudSaveGameState(novoState);

    setActiveTab("jogo");
    showToast("Sorteio realizado! As equipes equilibradas estão prontas.");
  };

  const handleStartRachao = (newState: GameState) => {
    setGameState(newState);
    setMatchSeconds(0);
    setIsTimerRunning(true);
    cloudSaveGameState(newState);
    showToast("Rachão Iniciado! Que vença o melhor!");
  };

  const handleEndRachao = () => {
    if (gameState.emAndamento) {
      const novaSessao: SavedSession = {
        id: `sessao_${Date.now()}`,
        date: new Date().toLocaleDateString("pt-BR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }),
        timestamp: Date.now(),
        gameState,
        duplas: globalDuplas,
        historico,
        matchSeconds,
        status: "encerrado",
      };
      saveSessoes([novaSessao, ...sessoes]);
    }

    const defaultState: GameState = {
      emAndamento: false,
      jogoAtual: null,
      primeiraProxima: null,
      equipeIncompleta: "",
      fila: [],
    };
    setGameState(defaultState);
    setIsTimerRunning(false);
    setMatchSeconds(0);
    cloudSaveGameState(defaultState);

    globalDuplas.forEach((d) => cloudDeleteDupla(d.id));
    setDuplas([]);

    showToast("Racha encerrado! A sessão foi arquivada e o painel está pronto para novos cadastros.");
  };

  const handleReactivateSession = (session: SavedSession) => {
    setGameState(session.gameState);
    setDuplas(session.duplas);
    setHistorico(session.historico);
    setMatchSeconds(session.matchSeconds || 0);
    setIsTimerRunning(true);
    cloudSaveGameState(session.gameState);
    session.duplas.forEach((d) => cloudSaveDupla(d));
    setActiveTab("jogo");
    showToast(`Sessão de ${session.date} reativada com sucesso!`, "success");
  };

  const handleDeleteSession = (sessionId: string) => {
    const updated = sessoes.filter((s) => s.id !== sessionId);
    saveSessoes(updated);
    showToast("Sessão excluída do histórico.");
  };

  const handleClearAllSessions = () => {
    saveSessoes([]);
    showToast("Histórico de sessões limpo com sucesso.");
  };

  const handleConfirmRound = (
    vencedoraId: string,
    jogadorTransferido: string,
    scoreTeam1?: number,
    scoreTeam2?: number
  ) => {
    const result = computeNextRachaoState({
      gameState,
      duplas,
      vencedoraId,
      jogadorTransferido,
      scoreTeam1,
      scoreTeam2,
      totalRodadasAnteriores: historico.length,
    });

    if (!result) return;

    const { novoGameState, novasDuplas, novoHistoricoItem, equipesSalvar } = result;

    setDuplas(novasDuplas);
    const novoHistorico = [...historico, novoHistoricoItem];
    setHistorico(novoHistorico);
    setGameState(novoGameState);

    equipesSalvar.forEach((eq) => cloudSaveDupla(eq));
    cloudAddHistorico(novoHistoricoItem);
    cloudSaveGameState(novoGameState);

    setMatchSeconds(0);
    showToast(`Rodada #${novoHistoricoItem.rodada} finalizada com sucesso!`);
  };

  const handleUpdateQueue = (newFilaIds: string[]) => {
    const updatedState = { ...gameState, fila: newFilaIds };
    setGameState(updatedState);
    cloudSaveGameState(updatedState);
  };

  const handleUpdateDuplas = (novasDuplas: Dupla[]) => {
    setDuplas(novasDuplas);
    novasDuplas.forEach((d) => {
      cloudSaveDupla(d);
    });
  };

  const handleUpdateGameState = (novoState: GameState) => {
    setGameState(novoState);
    cloudSaveGameState(novoState);
  };

  const handleSaveTorneio = (torneioInput: Tournament) => {
    const uid = getEffectiveCloudUid();
    const torneio: Tournament = {
      ...torneioInput,
      organizerUserId: torneioInput.organizerUserId || uid || "local_organizer",
    };

    setDuplas((prevDuplas) => {
      return prevDuplas.map((d) => {
        if (torneio.equipes.includes(d.id) && d.tournamentId !== torneio.id) {
          const updated = { ...d, tournamentId: torneio.id };
          if (isFirebaseConfigured() && uid) {
            saveDuplaFirestore(uid, updated, torneio.organizerUserId);
          }
          return updated;
        }
        return d;
      });
    });

    setTorneios((prev) => {
      const exists = prev.some((t) => t.id === torneio.id);
      if (exists) {
        return prev.map((t) => (t.id === torneio.id ? torneio : t));
      } else {
        return [torneio, ...prev];
      }
    });

    if (isFirebaseConfigured()) {
      saveTorneioFirestore(uid, torneio);
    }

    try {
      const courtsList = StorageService.get<any[]>(STORAGE_KEYS.RACHAO_COURTS, null);
      if (courtsList) {
        let updatedAny = false;
        const orgCpf = torneio.organizerCpf ? torneio.organizerCpf.replace(/\D/g, "") : "";
        const orgEmail = (torneio.organizerEmail || "").trim().toLowerCase();

        const newCourtsList = courtsList.map((c: any) => {
          if (!c.slots) return c;
          let courtUpdated = false;
          const newSlots = c.slots.map((s: any) => {
            if (s.isTournamentSlot) {
              const sCpf = s.organizerCpf ? s.organizerCpf.replace(/\D/g, "") : "";
              const sEmail = (s.organizerEmail || "").trim().toLowerCase();

              const matchesCpf = orgCpf && sCpf && orgCpf === sCpf;
              const matchesEmail = orgEmail && sEmail && orgEmail === sEmail;

              if (matchesCpf || matchesEmail) {
                courtUpdated = true;
                updatedAny = true;
                return {
                  ...s,
                  linkedTournamentId: torneio.id,
                  pendingTournamentLink: false,
                  bookedBy: `🏆 [TORNEIO] ${torneio.name} (${s.organizerName || torneio.organizerName || "Organizador"})`,
                };
              }
            }
            return s;
          });

          return courtUpdated ? { ...c, slots: newSlots } : c;
        });

        if (updatedAny) {
          StorageService.set(STORAGE_KEYS.RACHAO_COURTS, newCourtsList);
          showToast(`🏆 Agendamento da Arena vinculado automaticamente ao Torneio "${torneio.name}"!`, "success");
        }
      }
    } catch (err) {
      console.error("Erro ao auto-vincular quadra ao torneio:", err);
    }

    try {
      const channel = new BroadcastChannel("rachao_realtime_sync");
      channel.postMessage({ type: "TORNEIO_UPDATED", torneio });
      channel.close();
    } catch (e) {}
  };

  const handleDeleteTorneio = (id: string) => {
    const uid = getEffectiveCloudUid();
    setDuplas((prev) => {
      const teamsToDelete = prev.filter((d) => d.tournamentId === id);
      teamsToDelete.forEach((d) => {
        if (isFirebaseConfigured() && uid) {
          deleteDuplaFirestore(uid, d.id);
        }
      });
      return prev.filter((d) => d.tournamentId !== id);
    });

    setTorneios((prev) => prev.filter((t) => t.id !== id));
    if (isFirebaseConfigured() && uid) {
      deleteTorneioFirestore(uid, id);
    }
  };

  const handleImportJSON = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileReader = new FileReader();
    if (e.target.files && e.target.files[0]) {
      fileReader.readAsText(e.target.files[0], "UTF-8");
      fileReader.onload = (event) => {
        try {
          const parsed = JSON.parse(event.target?.result as string);
          if (parsed && Array.isArray(parsed.duplas)) {
            setDuplas(parsed.duplas);
            if (parsed.gameState) setGameState(parsed.gameState);
            if (parsed.historico) setHistorico(parsed.historico);

            if (isFirebaseConfigured() && user) {
              parsed.duplas.forEach((d: Dupla) => cloudSaveDupla(d));
              if (parsed.gameState) cloudSaveGameState(parsed.gameState);
              if (parsed.historico) {
                cloudClearHistorico();
                parsed.historico.forEach((h: HistoricoRodada) => cloudAddHistorico(h));
              }
            }
            showToast("Backup importado com sucesso!");
          } else {
            showToast("Estrutura do arquivo de backup inválida!", "error");
          }
        } catch (err) {
          showToast("Erro ao processar o arquivo JSON!", "error");
        }
      };
    }
  };

  const handleClearHistory = () => {
    setHistorico([]);
    cloudClearHistorico();
    showToast("Histórico de partidas reiniciado!");
  };

  const handleResetAllData = () => {
    if (
      window.confirm(
        "Deseja realmente LIMPAR TODOS os dados do Rachão? Isso excluirá todas as equipes, histórico e voltará o painel para a configuração inicial."
      )
    ) {
      StorageService.remove(STORAGE_KEYS.RACHAO_DUPLAS);
      StorageService.remove(STORAGE_KEYS.RACHAO_GAME_STATE);
      StorageService.remove(STORAGE_KEYS.RACHAO_HISTORICO);

      const duplasToDelete = [...duplas];

      setDuplas([]);
      setHistorico([]);

      const defaultState: GameState = {
        emAndamento: false,
        jogoAtual: null,
        primeiraProxima: null,
        equipeIncompleta: "",
        fila: [],
        vitoriasConsecutivas: 0,
        idEquipeVencedoraAtual: null,
        regras: {
          tamanhoEquipe: 2,
          modoPlay: "solo",
          limiteVitorias: 0,
          destinoVencedorLimite: "primeira_proxima",
        },
      };
      setGameState(defaultState);

      cloudSaveGameState(defaultState);
      cloudClearHistorico();
      duplasToDelete.forEach((d) => cloudDeleteDupla(d.id));

      showToast("Tudo limpo! Pronto para cadastrar manualmente.");
    }
  };

  const formatTime = (totalSecs: number) => {
    const mins = Math.floor(totalSecs / 60);
    const secs = totalSecs % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const exportJSON = () => exportToJSON(globalDuplas, gameState, historico);
  const exportExcel = () => exportToExcel(historico, globalDuplas);

  const value = useMemo(
    () => ({
      isInitialized,
      duplas,
      globalDuplas,
      gameState,
      setGameState,
      historico,
      torneios,
      sessoes,
      matchSeconds,
      isTimerRunning,
      formatTime,
      getEffectiveCloudUid,
      handleAddDupla,
      handleEditDupla,
      handleDeleteDupla,
      handleDuplicateDupla,
      handleApplySorteio,
      handleStartRachao,
      handleEndRachao,
      handleReactivateSession,
      handleDeleteSession,
      handleClearAllSessions,
      handleConfirmRound,
      handleUpdateQueue,
      handleUpdateDuplas,
      handleUpdateGameState,
      handleSaveTorneio,
      handleDeleteTorneio,
      handleImportJSON,
      handleClearHistory,
      handleResetAllData,
      exportJSON,
      exportExcel,
    }),
    [
      isInitialized,
      duplas,
      globalDuplas,
      gameState,
      historico,
      torneios,
      sessoes,
      matchSeconds,
      isTimerRunning,
    ]
  );

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
};

export const useGame = (): GameContextType => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error("useGame deve ser usado dentro de um GameProvider");
  }
  return context;
};
